import { defineStore } from 'pinia';

export interface CartItem {
  productId: string;
  // The specific marketplace offer this line is for. Different offers of the
  // same product are separate cart lines (different sellers, different prices).
  // Optional for backward-compat with cart entries saved before per-offer
  // purchasing — those continue to dedupe by productId alone.
  offerId?: string;
  merchantName?: string;
  name: string;
  priceCents: number;
  coverUrl: string | null;
  imageUrl: string | null;
  platform: string | null;
  // Kinguin activation region label (see utils/activation-region). Optional:
  // lines saved before it existed show no region pill.
  region?: string | null;
  // Max units we can buy of this SKU — copied from the offer's available qty
  // (or product.totalQty when no offer was chosen) at the time of add.
  maxQty: number;
  qty: number;
}

interface State {
  items: CartItem[];
}

// Per-transaction cap surfaced as N/X in the cart UI. Spec calls this the
// "Kinguin API purchase limit per transaction" — exact value depends on the
// merchant tier. 10 is a conservative working value; bump or wire to env when
// the real limit is confirmed.
export const KINGUIN_ORDER_LIMIT = 10;

// Per-user storage. v1 was a single global key — that leaked carts between
// users on the same browser. v2 keys by user ID so each session starts with
// its own list, and an anonymous user has no persisted cart at all.
const STORAGE_PREFIX = 'ogkeys.cart.v2.';
const LEGACY_GLOBAL_KEY = 'ogkeys.cart.v1';

let activeStorageKey: string | null = null;

function lineKey(productId: string, offerId: string | undefined): string {
  return `${productId}::${offerId ?? ''}`;
}

function matches(item: CartItem, productId: string, offerId: string | undefined): boolean {
  if (item.productId !== productId) return false;
  // Treat missing offerId on both sides as equal — legacy entries stay
  // dedup-able by productId alone.
  return (item.offerId ?? null) === (offerId ?? null);
}

function loadFromStorage(key: string | null): CartItem[] {
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCartItem);
  } catch {
    return [];
  }
}

function isCartItem(x: unknown): x is CartItem {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.productId === 'string' &&
    typeof o.name === 'string' &&
    typeof o.priceCents === 'number' &&
    typeof o.qty === 'number' &&
    typeof o.maxQty === 'number'
  );
}

function persist(items: CartItem[]) {
  if (!activeStorageKey) return;
  try {
    localStorage.setItem(activeStorageKey, JSON.stringify(items));
  } catch {
    // private mode, quota — silently skip; cart will just be in-memory
  }
}

export const useCartStore = defineStore('cart', {
  // Always boot empty — App.vue calls setUser() once auth has resolved
  // (and again on every user change), which is what loads the right key.
  state: (): State => ({ items: [] }),
  getters: {
    count: (s) => s.items.reduce((n, i) => n + i.qty, 0),
    subtotalCents: (s) => s.items.reduce((n, i) => n + i.priceCents * i.qty, 0),
    isEmpty: (s) => s.items.length === 0,
    isAtLimit(): boolean {
      return this.count >= KINGUIN_ORDER_LIMIT;
    },
    remainingSlots(): number {
      return Math.max(0, KINGUIN_ORDER_LIMIT - this.count);
    },
  },
  actions: {
    /**
     * Switch the active cart to the given user — `null` means signed-out
     * (no persistence, empty in-memory cart). Called from App.vue on auth
     * mount and every user change. Also evicts the legacy global v1 key
     * the first time a user is set, so it can never leak again.
     */
    setUser(userId: string | null) {
      const newKey = userId ? `${STORAGE_PREFIX}${userId}` : null;
      if (newKey === activeStorageKey) return;
      activeStorageKey = newKey;
      this.items = loadFromStorage(newKey);
      try { localStorage.removeItem(LEGACY_GLOBAL_KEY); } catch { /* ignore */ }
    },
    keyOf(item: Pick<CartItem, 'productId' | 'offerId'>): string {
      return lineKey(item.productId, item.offerId);
    },
    /**
     * Add `qty` units of a product+offer combination. Items with different
     * offerIds are separate lines. `isNew` tells the caller whether this
     * created a brand-new cart line (vs. bumping qty on an existing one) —
     * used to trigger "added to cart" feedback only for genuinely new lines.
     */
    add(item: Omit<CartItem, 'qty'>, qty = 1): { ok: boolean; isNew: boolean } {
      if (qty < 1) return { ok: false, isNew: false };
      const remaining = this.remainingSlots;
      if (remaining <= 0) return { ok: false, isNew: false };
      const grantedQty = Math.min(qty, remaining);
      const existing = this.items.find((i) => matches(i, item.productId, item.offerId));
      if (existing) {
        existing.qty = Math.min(item.maxQty, existing.qty + grantedQty);
        // Refresh denormalized fields in case price/cover/merchant changed.
        existing.name = item.name;
        existing.priceCents = item.priceCents;
        existing.coverUrl = item.coverUrl;
        existing.imageUrl = item.imageUrl;
        existing.platform = item.platform;
        existing.maxQty = item.maxQty;
        existing.merchantName = item.merchantName;
      } else {
        this.items.push({ ...item, qty: Math.min(grantedQty, item.maxQty) });
      }
      persist(this.items);
      return { ok: true, isNew: !existing };
    },
    remove(productId: string, offerId?: string) {
      this.items = this.items.filter((i) => !matches(i, productId, offerId));
      persist(this.items);
    },
    setQty(productId: string, qty: number, offerId?: string) {
      const item = this.items.find((i) => matches(i, productId, offerId));
      if (!item) return;
      const otherQty = this.count - item.qty;
      const max = Math.min(item.maxQty, KINGUIN_ORDER_LIMIT - otherQty);
      item.qty = Math.max(1, Math.min(qty, max));
      persist(this.items);
    },
    clear() {
      this.items = [];
      if (activeStorageKey) {
        try { localStorage.removeItem(activeStorageKey); } catch { /* ignore */ }
      }
    },
  },
});
